import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import { ReportFormatHelper } from '../helpers/ReportFormatHelper';
import type {
  ITableColumn,
  ITableModel,
  ITableRowModel,
  TableCell as TableCellValue,
  TableColumnFormat,
} from '../types/tables';

interface IModelTableProps {
  model: ITableModel;
  currency: string;
  /** Emphasise rows flagged `meta.large` (variance table). */
  flagRows?: boolean;
}

const INDENT_CLASSES = ['pl-md', 'pl-2xl', 'pl-4xl'];

const formatCell = (
  cell: TableCellValue,
  format: TableColumnFormat,
  currency: string
): string => {
  if (cell === null) return '—';
  if (typeof cell === 'string') return cell;
  switch (format) {
    case 'currency':
      return ReportFormatHelper.currency(cell, currency);
    case 'delta':
      return ReportFormatHelper.delta(cell, currency);
    case 'percent':
      return ReportFormatHelper.percent(cell);
    case 'signedPercent':
      return ReportFormatHelper.percent(cell, 1, true);
    case 'days':
      return ReportFormatHelper.days(cell);
    default:
      return String(cell);
  }
};

const deltaTone = (
  row: ITableRowModel,
  column: ITableColumn,
  cell: TableCellValue
): string | null => {
  const isDelta =
    column.format === 'delta' || column.format === 'signedPercent';
  if (!isDelta || typeof cell !== 'number' || cell === 0) return null;
  const favourable = row.meta?.favourable;
  if (favourable === undefined) {
    return cell < 0 && column.format === 'delta' ? 'text-text-error' : null;
  }
  return favourable ? 'text-text-success' : 'text-text-error';
};

export const ModelTable = ({
  model,
  currency,
  flagRows = false,
}: IModelTableProps) => {
  const [labelColumn, ...valueColumns] = model.columns;
  return (
    <Table className="txt-extra-small-regular" disableContainer>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="h-7 pl-md text-text-tertiary">
            {labelColumn?.label}
          </TableHead>
          {valueColumns.map((c) => (
            <TableHead
              key={c.key}
              className={cn(
                'h-7 whitespace-nowrap text-text-tertiary',
                c.format !== 'text' && 'text-right'
              )}
            >
              {c.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {model.rows.map((row) => {
          const isHeader = row.kind === 'header';
          return (
            <TableRow
              key={row.key}
              className={cn(
                isHeader && 'bg-bg-secondary',
                row.kind === 'total' &&
                  'border-t border-border-primary txt-extra-small-bold',
                row.kind === 'subtotal' && 'txt-extra-small-medium'
              )}
            >
              <TableCell
                className={cn(
                  'py-sm',
                  INDENT_CLASSES[
                    Math.min(row.indent ?? 0, INDENT_CLASSES.length - 1)
                  ],
                  isHeader && 'uppercase tracking-wide text-text-tertiary'
                )}
              >
                {row.label}
              </TableCell>
              {valueColumns.map((c, i) => {
                const cell = row.cells[i] ?? null;
                return (
                  <TableCell
                    key={c.key}
                    className={cn(
                      'py-sm tabular-nums',
                      c.format !== 'text' && 'text-right',
                      deltaTone(row, c, cell),
                      flagRows &&
                        row.meta?.large === true &&
                        'txt-extra-small-bold'
                    )}
                  >
                    {isHeader ? '' : formatCell(cell, c.format, currency)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
